import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import { useApprovals, useAudit } from "../../hooks/manage";
import { presetRange, ymd } from "../../hooks/reports";
import { api } from "../../lib/api";
import { money } from "../../lib/format";
import { Empty, Pill, SectionHeader } from "../ui/Badges";

const PRESETS = ["today", "yesterday", "week", "month"] as const;
type Check = { intact: boolean; entries?: number; firstBrokenSeq?: number | null };

/** Integrity: the waiver and HR ledgers are hash-chained; this proves nobody edited them behind the app's back. */
function Integrity() {
  const { t } = useTranslation();
  const { toastError } = useToast();
  const { isAdmin } = useAuth();
  const [res, setRes] = useState<Record<string, Check | "busy">>({});
  async function run(key: string, path: string) {
    setRes((r) => ({ ...r, [key]: "busy" }));
    try {
      const out = await api.get<Check>(path);
      setRes((r) => ({ ...r, [key]: out }));
    } catch (e) {
      setRes((r) => { const n = { ...r }; delete n[key]; return n; });
      toastError(e);
    }
  }
  const items = [
    ...(isAdmin ? [{ key: "waivers", path: "/waivers/audit/verify" }] : []),
    ...(isAdmin ? [{ key: "hr", path: "/hr/audit/verify" }] : []),
  ];
  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold text-gray-600">{t("audit.integrityHelp")}</p>
      {items.map((i) => {
        const r = res[i.key];
        return (
          <div key={i.key} className="card flex flex-wrap items-center justify-between gap-3 p-3.5" data-check={i.key}>
            <div><div className="text-sm font-black">{t(`audit.check.${i.key}`)}</div><div className="text-[11px] text-gray-500">{t(`audit.check.${i.key}Help`)}</div></div>
            <div className="flex items-center gap-3">
              {r && r !== "busy" && (r.intact ? <Pill tone="green">✓ {t("audit.intact")}</Pill> : <Pill tone="red">⚠ {t("audit.broken", { n: r.firstBrokenSeq ?? 0 })}</Pill>)}
              <button type="button" className="btn-ghost" disabled={r === "busy"} onClick={() => void run(i.key, i.path)}>{r === "busy" ? t("common.loading") : t("audit.verify")}</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Audit & approvals: who did what, every manager approval (granted or refused), and the ledger integrity checks. */
export function AuditPanel() {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState<"trail" | "approvals" | "integrity">("trail");
  const [range, setRange] = useState(presetRange("today"));
  const [preset, setPreset] = useState<string>("today");
  const [f, setF] = useState({ q: "", prefix: "", staffId: "", page: 1, scope: "branch" });
  const [open, setOpen] = useState<string | null>(null);
  const trail = useAudit({ ...range, ...f });
  const appr = useApprovals(range.from, range.to, f.scope);
  const a = trail.data;
  const pages = a ? Math.max(1, Math.ceil(a.total / 50)) : 1;
  const set = (p: Partial<typeof f>) => setF({ ...f, page: 1, ...p });
  return (
    <div className="space-y-5">
      <div className="card flex items-center gap-3.5 p-5">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-tr from-cosmic-purple to-solar-orange text-2xl shadow-cosmic" aria-hidden>🛡️</div>
        <div><h1 className="text-xl font-black tracking-tight">{t("audit.title")}</h1><p className="text-xs font-medium text-gray-500">{t("audit.subtitle")}</p></div>
      </div>
      <div className="flex gap-1.5" role="tablist" aria-label={t("audit.title")}>
        {(["trail", "approvals", "integrity"] as const).map((x) => <button key={x} role="tab" type="button" aria-selected={tab === x} onClick={() => setTab(x)} className={`rounded-xl px-4 py-2 text-xs font-black ${tab === x ? "bg-cosmic-purple text-white shadow-cosmic" : "bg-white text-gray-600 hover:bg-purple-50"}`}>{t(`audit.tabs.${x}`)}</button>)}
      </div>
      <section className="card space-y-3 p-4 sm:p-5" aria-label={t(`audit.tabs.${tab}`)}>
        <SectionHeader title={t(`audit.tabs.${tab}`)} />
        {tab !== "integrity" && (
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-wrap gap-1 text-[11px] font-black" role="group" aria-label={t("reports.period")}>
              {PRESETS.map((p) => <button key={p} type="button" aria-pressed={preset === p} onClick={() => { setPreset(p); setRange(presetRange(p)); setF({ ...f, page: 1 }); }} className={`rounded-lg px-2.5 py-1 ${preset === p ? "bg-cosmic-navy text-white" : "bg-gray-100 text-gray-700"}`}>{t(`reports.presets.${p}`)}</button>)}
            </div>
            <div><label className="label" htmlFor="au-from">{t("reports.from")}</label><input id="au-from" className="field num !py-1.5" type="date" value={range.from} max={ymd(new Date())} onChange={(e) => { setPreset("custom"); setRange({ ...range, from: e.target.value }); setF({ ...f, page: 1 }); }} /></div>
            <div><label className="label" htmlFor="au-to">{t("reports.to")}</label><input id="au-to" className="field num !py-1.5" type="date" value={range.to} max={ymd(new Date())} onChange={(e) => { setPreset("custom"); setRange({ ...range, to: e.target.value }); setF({ ...f, page: 1 }); }} /></div>
            {tab === "trail" && (
              <>
                <input className="field !w-48 !py-1.5" aria-label={t("audit.search")} placeholder={t("audit.search")} value={f.q} onChange={(e) => set({ q: e.target.value })} />
                <select className="field !w-auto !py-1.5" aria-label={t("audit.group")} value={f.prefix} onChange={(e) => set({ prefix: e.target.value })}><option value="">{t("audit.anyGroup")}</option>{(a?.groups ?? []).map((g) => <option key={g.grp} value={g.grp}>{g.grp} ({g.n})</option>)}</select>
                <select className="field !w-auto !py-1.5" aria-label={t("audit.who")} value={f.staffId} onChange={(e) => set({ staffId: e.target.value })}><option value="">{t("audit.anyone")}</option>{(a?.staff ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
              </>
            )}
            {isAdmin && <select className="field !w-auto !py-1.5" aria-label={t("reports.scope")} value={f.scope} onChange={(e) => set({ scope: e.target.value })}><option value="branch">{t("reports.thisBranch")}</option><option value="all">{t("reports.allBranches")}</option></select>}
          </div>
        )}
        {tab === "trail" && (trail.isLoading ? <Empty>{t("common.loading")}</Empty> : !a?.entries.length ? <Empty>{t("audit.none")}</Empty> : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-start text-xs">
                <thead className="border-b border-gray-100 bg-gray-50 text-[10px] font-black uppercase tracking-wider text-gray-500"><tr><th className="px-3 py-2 text-start">{t("audit.when")}</th><th className="px-2 py-2 text-start">{t("audit.who")}</th><th className="px-2 py-2 text-start">{t("audit.action")}</th><th className="px-2 py-2 text-start">{t("audit.entity")}</th><th className="px-3 py-2" /></tr></thead>
                <tbody className="divide-y divide-gray-100 font-bold text-gray-700">
                  {a.entries.map((e) => (
                    <Fragment key={e.id}>
                      <tr data-audit={e.action}>
                        <td className="num px-3 py-2 font-mono">{e.at}</td><td className="px-2 py-2">{e.staff}<span className="ms-1 text-[10px] text-gray-400">{e.branch}</span></td>
                        <td className="px-2 py-2 font-mono text-cosmic-purple">{e.action}</td><td className="px-2 py-2 text-gray-500">{e.entity}</td>
                        <td className="px-3 py-2 text-end"><button type="button" className="btn-ghost !px-2 !py-0.5 text-[11px]" aria-expanded={open === e.id} onClick={() => setOpen(open === e.id ? null : e.id)}>{t("audit.detail")}</button></td>
                      </tr>
                      {open === e.id && <tr><td colSpan={5} className="bg-gray-50 px-3 py-2"><pre dir="ltr" className="whitespace-pre-wrap break-all font-mono text-[11px] text-gray-700">{JSON.stringify(e.detail, null, 2)}{e.entity_id ? `\n#${e.entity_id}` : ""}</pre></td></tr>}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between text-xs font-bold text-gray-600">
              <span>{t("audit.total", { n: a.total })}</span>
              <span className="flex items-center gap-2">
                <button type="button" className="btn-ghost !px-3 !py-1" disabled={f.page <= 1} onClick={() => setF({ ...f, page: f.page - 1 })}>‹</button>
                <span className="num">{f.page} / {pages}</span>
                <button type="button" className="btn-ghost !px-3 !py-1" disabled={f.page >= pages} onClick={() => setF({ ...f, page: f.page + 1 })}>›</button>
              </span>
            </div>
          </>
        ))}
        {tab === "approvals" && (appr.isLoading ? <Empty>{t("common.loading")}</Empty> : !appr.data?.events.length ? <Empty>{t("audit.noApprovals")}</Empty> : (
          <>
            <p className="text-xs font-black text-gray-600">{t("audit.denied")}: <span className="num font-mono text-red-600">{appr.data.denied}</span></p>
            <div className="overflow-x-auto">
              <table className="w-full text-start text-xs">
                <thead className="border-b border-gray-100 bg-gray-50 text-[10px] font-black uppercase tracking-wider text-gray-500"><tr><th className="px-3 py-2 text-start">{t("audit.when")}</th><th className="px-2 py-2 text-start">{t("audit.action")}</th><th className="px-2 py-2 text-start">{t("audit.requestedBy")}</th><th className="px-2 py-2 text-start">{t("audit.approvedBy")}</th><th className="px-2 py-2 text-end">{t("audit.amount")}</th><th className="px-2 py-2 text-start">{t("audit.reason")}</th><th className="px-3 py-2 text-center">{t("audit.result")}</th></tr></thead>
                <tbody className="divide-y divide-gray-100 font-bold text-gray-700">
                  {appr.data.events.map((e) => (
                    <tr key={e.id} data-approval={e.action}>
                      <td className="num px-3 py-2 font-mono">{e.at}</td><td className="px-2 py-2">{t(`audit.act.${e.action}`, { defaultValue: e.action })}</td><td className="px-2 py-2">{e.requested_by}</td><td className="px-2 py-2">{e.approved_by ?? "—"}</td>
                      <td className="num px-2 py-2 text-end font-mono">{e.amount === null ? "—" : money(e.amount)}</td><td className="px-2 py-2 text-gray-500">{e.reason ?? ""}</td>
                      <td className="px-3 py-2 text-center"><Pill tone={e.granted ? "green" : "red"}>{e.granted ? t("audit.granted") : t("audit.refused")}</Pill></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ))}
        {tab === "integrity" && <Integrity />}
      </section>
    </div>
  );
}
